/**
 * Passcode Manager Service
 * 
 * Securely manages passcode in sessionStorage (encrypted)
 * Used for encryption operations during active session
 * Survives page refreshes but cleared on browser close
 */

import CryptoJS from 'crypto-js';

class PasscodeManager {
  constructor() {
    this.maxAge = 30 * 60 * 1000; // 30 minutes
    this.storageKey = 'eternavaults_encryption_passcodes';
    this.encryptionKey = 'eternavaults-passcode-storage-v1'; // Simple obfuscation key
  }

  /**
   * Store passcode for a member (encrypted in sessionStorage)
   * @param {string} memberId - Member identifier
   * @param {string} passcode - Passcode to store
   */
  setPasscode(memberId, passcode) {
    if (!memberId || !passcode) {
      console.error('❌ setPasscode called with invalid params:', { memberId, hasPasscode: !!passcode });
      return;
    }
    
    // Clear old entries periodically
    this.cleanup();
    
    // Convert to string to ensure consistent key format
    const key = String(memberId);
    console.log('🔐 PasscodeManager.setPasscode: Storing passcode for member:', key);
    console.log('🔐 PasscodeManager.setPasscode: Passcode length:', passcode.length);
    
    try {
      // Get existing store
      const store = this.getStore();
      console.log('📦 PasscodeManager.setPasscode: Current store keys:', Object.keys(store));
      
      // Encrypt passcode (simple obfuscation - not true security since key is in code)
      const encrypted = CryptoJS.AES.encrypt(passcode, this.encryptionKey + key).toString();
      console.log('🔒 PasscodeManager.setPasscode: Encryption successful, length:', encrypted.length);
      
      // Store with timestamp
      store[key] = {
        encrypted: encrypted,
        timestamp: Date.now()
      };
      
      // Save back to sessionStorage
      sessionStorage.setItem(this.storageKey, JSON.stringify(store));
      console.log('✅ PasscodeManager.setPasscode: Passcode stored in sessionStorage');
      console.log('📋 PasscodeManager.setPasscode: All stored passcode keys:', Object.keys(store));
      
      // Verify it was stored
      const verify = sessionStorage.getItem(this.storageKey);
      console.log('🔍 PasscodeManager.setPasscode: Verification - sessionStorage has data?', !!verify);
    } catch (error) {
      console.error('❌ PasscodeManager.setPasscode: Failed to store passcode:', error);
      console.error('❌ Error details:', error.message, error.stack);
    }
  }
  
  /**
   * Get store from sessionStorage
   * @returns {Object} Store object
   */
  getStore() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to get store:', error);
      return {};
    }
  }

  /**
   * Get passcode for a member
   * @param {string} memberId - Member identifier
   * @returns {string|null} Passcode if available and not expired
   */
  getPasscode(memberId) {
    // Convert to string to ensure consistent key format
    const key = String(memberId);
    
    try {
      const store = this.getStore();      
      const entry = store[key];
      
      if (!entry) {
        return null;
      }

      // Check if expired
      const age = Date.now() - entry.timestamp;
      if (age > this.maxAge) {
        console.log('⏰ Passcode expired for key:', key);
        delete store[key];
        sessionStorage.setItem(this.storageKey, JSON.stringify(store));
        return null;
      }

      // Decrypt passcode
      try {
        const decrypted = CryptoJS.AES.decrypt(entry.encrypted, this.encryptionKey + key);
        const passcode = decrypted.toString(CryptoJS.enc.Utf8);
        
        if (!passcode) {
          console.error('Failed to decrypt passcode for key:', key);
          return null;
        }
        
        console.log('✅ Passcode found and decrypted for key:', key);
        return passcode;
      } catch (decryptError) {
        console.error('Decryption failed for key:', key, decryptError);
        return null;
      }
    } catch (error) {
      console.error('Failed to get passcode:', error);
      return null;
    }
  }

  /**
   * Clear passcode for a member
   * @param {string} memberId - Member identifier
   */
  clearPasscode(memberId) {
    const key = String(memberId);
    const store = this.getStore();
    delete store[key];
    sessionStorage.setItem(this.storageKey, JSON.stringify(store));
  }

  /**
   * Clear all passcodes
   */
  clearAll() {
    sessionStorage.removeItem(this.storageKey);
  }

  /**
   * Cleanup expired entries
   */
  cleanup() {
    const now = Date.now();
    const store = this.getStore();
    let updated = false;
    
    for (const [memberId, entry] of Object.entries(store)) {
      if (now - entry.timestamp > this.maxAge) {
        delete store[memberId];
        updated = true;
      }
    }
    
    if (updated) {
      sessionStorage.setItem(this.storageKey, JSON.stringify(store));
    }
  }

  /**
   * Check if passcode is available for a member
   * @param {string} memberId - Member identifier
   * @returns {boolean} True if passcode is available
   */
  hasPasscode(memberId) {
    return this.getPasscode(memberId) !== null;
  }
}

// Create singleton instance
const passcodeManager = new PasscodeManager();

export default passcodeManager;

